import { createFileRoute } from '@tanstack/react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { ArrowRight, BookOpen, KeyRound, Layers, ShieldCheck, Webhook, Zap } from 'lucide-react';
import { Badge, Button, Card, CardContent } from '@caratuva/ui';
import { Section, SectionHeading } from '../components/Section.tsx';
import { useSalesContact } from '../components/SalesContact.tsx';
import { API_DOCS_URL, DOCS_URL, SIGNUP_URL } from '../lib/links.ts';
import { useRouteMeta } from '../lib/useDocumentMeta.ts';

export const Route = createFileRoute('/integrators')({
  component: IntegratorsPage,
});

type IconType = typeof Zap;

function FeatureCard({
  icon: Icon,
  titleId,
  bodyId,
}: {
  icon: IconType;
  titleId: string;
  bodyId: string;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-md bg-[var(--color-navy-50)] text-[var(--color-navy-600)]">
          <Icon size={20} strokeWidth={1.75} aria-hidden="true" />
        </div>
        <h3 className="text-base font-semibold text-[var(--color-navy-950)]">
          <FormattedMessage id={titleId} />
        </h3>
        <p className="mt-2 text-sm text-[var(--color-navy-700)]">
          <FormattedMessage id={bodyId} />
        </p>
      </CardContent>
    </Card>
  );
}

function Hero() {
  const intl = useIntl();
  return (
    <Section tint="white" className="pt-20 sm:pt-24">
      <div className="mx-auto max-w-3xl text-center">
        <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-[var(--color-navy-600)]">
          <FormattedMessage id="integrators.eyebrow" />
        </p>
        <h1 className="text-4xl font-semibold tracking-tight text-[var(--color-navy-950)] sm:text-5xl">
          <FormattedMessage id="integrators.title" />
        </h1>
        <p className="mt-6 text-lg text-[var(--color-navy-700)]">
          <FormattedMessage id="integrators.subtitle" />
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button asChild size="lg">
            <a href={SIGNUP_URL}>{intl.formatMessage({ id: 'integrators.cta.primary' })}</a>
          </Button>
          <Button asChild size="lg" variant="outline">
            <a href={DOCS_URL}>{intl.formatMessage({ id: 'integrators.cta.secondary' })}</a>
          </Button>
        </div>
      </div>
    </Section>
  );
}

// Building blocks integrators embed: payment intents, connected sellers,
// webhooks, sandbox/test mode.
function Capabilities() {
  return (
    <Section tint="canvas">
      <SectionHeading
        title={<FormattedMessage id="integrators.caps.title" />}
        subtitle={<FormattedMessage id="integrators.caps.subtitle" />}
      />
      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <FeatureCard
          icon={Zap}
          titleId="integrators.caps.intents.title"
          bodyId="integrators.caps.intents.body"
        />
        <FeatureCard
          icon={Layers}
          titleId="integrators.caps.sellers.title"
          bodyId="integrators.caps.sellers.body"
        />
        <FeatureCard
          icon={Webhook}
          titleId="integrators.caps.webhooks.title"
          bodyId="integrators.caps.webhooks.body"
        />
        <FeatureCard
          icon={ShieldCheck}
          titleId="integrators.caps.compliance.title"
          bodyId="integrators.caps.compliance.body"
        />
      </div>
    </Section>
  );
}

// API-first section with a payment-intent snippet (carried over from the old
// home page's Developer block).
function Developer() {
  const intl = useIntl();
  const points = [
    { icon: KeyRound, id: 'integrators.dev.point.keys' },
    { icon: Webhook, id: 'integrators.dev.point.webhooks' },
    { icon: BookOpen, id: 'integrators.dev.point.openapi' },
  ];
  const snippet = `curl -X POST https://api.caratuva.com/v1/payments \\
  -H "X-API-Key: pk_test_..." \\
  -H "Content-Type: application/json" \\
  -d '{ "externalId": "INV-1042", "amount": "5000.00", "currency": "USD",
        "sellerExternalId": "seller-42",
        "buyer": { "email": "buyer@empresa.com.br" } }'`;
  return (
    <Section tint="white">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight text-[var(--color-navy-950)] sm:text-4xl">
            <FormattedMessage id="integrators.dev.title" />
          </h2>
          <p className="mt-4 text-lg text-[var(--color-navy-700)]">
            <FormattedMessage id="integrators.dev.subtitle" />
          </p>
          <ul className="mt-6 space-y-3">
            {points.map(({ icon: Icon, id }) => (
              <li key={id} className="flex gap-3 text-sm text-[var(--color-navy-800)]">
                <Icon
                  size={18}
                  strokeWidth={1.75}
                  className="mt-0.5 shrink-0 text-[var(--color-navy-600)]"
                  aria-hidden="true"
                />
                <FormattedMessage id={id} />
              </li>
            ))}
          </ul>
          <div className="mt-8">
            <Button asChild variant="outline">
              <a href={API_DOCS_URL}>
                {intl.formatMessage({ id: 'integrators.dev.cta' })}
                <ArrowRight size={16} aria-hidden="true" />
              </a>
            </Button>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-[var(--color-navy-800)] bg-[var(--color-navy-950)] shadow-sm">
          <div className="border-b border-[var(--color-navy-800)] px-4 py-2 text-xs font-medium text-[var(--color-beige-200)]">
            <FormattedMessage id="integrators.dev.snippet.caption" />
          </div>
          <pre className="overflow-x-auto px-4 py-4 text-xs leading-relaxed text-[var(--color-beige-100)]">
            <code style={{ fontFamily: 'var(--font-mono)' }}>{snippet}</code>
          </pre>
        </div>
      </div>
    </Section>
  );
}

function ModeColumn({
  titleId,
  badgeId,
  badgeVariant,
  items,
  ctaId,
  ctaHref,
  onCtaClick,
  ctaVariant,
  highlight,
}: {
  titleId: string;
  badgeId: string;
  badgeVariant: 'success' | 'warning';
  items: string[];
  ctaId: string;
  // Provide exactly one of ctaHref (link out) or onCtaClick (open the modal).
  ctaHref?: string;
  onCtaClick?: () => void;
  ctaVariant: 'default' | 'outline';
  highlight: boolean;
}) {
  const intl = useIntl();
  return (
    <Card className={highlight ? 'border-[var(--color-navy-300)]' : undefined}>
      <CardContent className="flex h-full flex-col pt-6">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-[var(--color-navy-950)]">
            <FormattedMessage id={titleId} />
          </h3>
          <Badge variant={badgeVariant}>
            <FormattedMessage id={badgeId} />
          </Badge>
        </div>
        <ul className="mt-6 flex-1 space-y-3">
          {items.map((id) => (
            <li key={id} className="flex gap-2 text-sm text-[var(--color-navy-700)]">
              <FormattedMessage id={id} />
            </li>
          ))}
        </ul>
        <div className="mt-6">
          {onCtaClick ? (
            <Button type="button" variant={ctaVariant} className="w-full" onClick={onCtaClick}>
              {intl.formatMessage({ id: ctaId })}
            </Button>
          ) : (
            <Button asChild variant={ctaVariant} className="w-full">
              <a href={ctaHref}>{intl.formatMessage({ id: ctaId })}</a>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Sandbox vs production (carried over from the old home page's Modes block).
function Modes() {
  const { open: openSalesContact } = useSalesContact();
  return (
    <Section id="modes" tint="canvas">
      <SectionHeading title={<FormattedMessage id="integrators.modes.title" />} />
      <div className="mx-auto mt-12 grid max-w-3xl gap-6 sm:grid-cols-2">
        <ModeColumn
          titleId="integrators.modes.sandbox.title"
          badgeId="integrators.modes.sandbox.badge"
          badgeVariant="success"
          items={[
            'integrators.modes.sandbox.item1',
            'integrators.modes.sandbox.item2',
            'integrators.modes.sandbox.item3',
          ]}
          ctaId="integrators.modes.sandbox.cta"
          ctaHref={SIGNUP_URL}
          ctaVariant="default"
          highlight
        />
        <ModeColumn
          titleId="integrators.modes.production.title"
          badgeId="integrators.modes.production.badge"
          badgeVariant="warning"
          items={[
            'integrators.modes.production.item1',
            'integrators.modes.production.item2',
            'integrators.modes.production.item3',
          ]}
          ctaId="integrators.modes.production.cta"
          onCtaClick={openSalesContact}
          ctaVariant="outline"
          highlight={false}
        />
      </div>
    </Section>
  );
}

function CtaBand() {
  const intl = useIntl();
  const { open: openSalesContact } = useSalesContact();
  return (
    <Section tint="brand">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="text-3xl font-semibold tracking-tight text-[var(--color-beige-50)] sm:text-4xl">
          <FormattedMessage id="integrators.ctaband.title" />
        </h2>
        <p className="mt-4 text-lg text-[var(--color-beige-200)]">
          <FormattedMessage id="integrators.ctaband.subtitle" />
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button asChild size="lg">
            <a href={SIGNUP_URL}>{intl.formatMessage({ id: 'integrators.ctaband.primary' })}</a>
          </Button>
          <Button
            type="button"
            size="lg"
            variant="outline"
            onClick={openSalesContact}
            className="border-[rgba(251,248,239,0.5)] bg-[rgba(251,248,239,0.1)] backdrop-blur-sm text-[var(--color-beige-50)] hover:bg-[rgba(251,248,239,0.2)] hover:text-[var(--color-beige-50)]"
          >
            {intl.formatMessage({ id: 'integrators.ctaband.secondary' })}
          </Button>
        </div>
      </div>
    </Section>
  );
}

function IntegratorsPage() {
  useRouteMeta('meta.integrators.title', 'meta.integrators.description', 'meta.integrators.social');
  return (
    <>
      <Hero />
      <Capabilities />
      <Developer />
      <Modes />
      <CtaBand />
    </>
  );
}
